import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatCurrency } from '@/lib/utils'
import type { ResumenMensual } from '@/hooks/useDashboard'

interface IngresosMensualesProps {
  data: ResumenMensual[]
  isLoading?: boolean
  /** Sin verCuentas la serie de gastos no se pide, así que tampoco se dibuja (ni su neto). */
  incluyeGastos?: boolean
}

const SERIES = [
  { key: 'ingresos', label: 'Ingresos', barClass: 'bg-primary/80 hover:bg-primary' },
  { key: 'gastos', label: 'Gastos', barClass: 'bg-orange-500/80 hover:bg-orange-500' },
  { key: 'neto', label: 'Neto', barClass: 'bg-blue-700/80 hover:bg-blue-700' },
] as const

export function IngresosMensuales({ data, isLoading, incluyeGastos = true }: IngresosMensualesProps) {
  const series = incluyeGastos ? SERIES : SERIES.slice(0, 1)
  const titulo = incluyeGastos ? 'Ingresos y Gastos' : 'Ingresos'

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{titulo}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-end gap-2">
            {[80, 120, 95, 140, 110, 130].map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-muted animate-pulse rounded-t"
                  style={{ height: `${h}px` }}
                />
                <div className="h-3 w-12 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  // El máximo puede caer en cualquiera de las series dibujadas (ej: un mes con más gasto que ingreso)
  const maxValue = Math.max(
    ...data.flatMap(d => series.map(s => Math.abs(d[s.key]))),
    1,
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{titulo}</CardTitle>
        <div className="flex gap-4 text-xs text-muted-foreground">
          {series.map(s => (
            <span key={s.key} className="flex items-center gap-1.5">
              <span className={`inline-block h-2 w-2 rounded-sm ${s.barClass.split(' ')[0]}`} />
              {s.label}
            </span>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            {incluyeGastos ? 'No hay datos de ingresos ni gastos' : 'No hay datos de ingresos'}
          </p>
        ) : (
          <div className="h-[200px] flex items-end gap-3">
            {data.map((item) => (
              <div key={item.mes} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end justify-center gap-1 h-[180px]">
                  {series.map(s => {
                    const valor = item[s.key]
                    // La altura va por valor absoluto (las barras crecen desde abajo), así que un
                    // neto negativo se vería idéntico a uno positivo: el rojo es lo que distingue
                    // el mes con pérdida del mes bueno. Detectado en el probe de DEV (ago 2026).
                    const esPerdida = valor < 0
                    const height = (Math.abs(valor) / maxValue) * 180
                    return (
                      <div
                        key={s.key}
                        className={`flex-1 rounded-t transition-colors cursor-pointer ${
                          esPerdida ? 'bg-red-600/80 hover:bg-red-600' : s.barClass
                        }`}
                        style={{ height: `${Math.max(height, 2)}px` }}
                        title={`${s.label} — ${item.mes}: ${formatCurrency(valor)}${
                          esPerdida ? ' (pérdida)' : ''
                        }`}
                      />
                    )
                  })}
                </div>
                <div className="text-xs text-muted-foreground text-center">
                  {item.mes}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
